import React, { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../config/api";
import {
  FaClock,
  FaStar,
  FaTrash,
  FaPlus,
  FaSave,
  FaCheckCircle,
  FaInfoCircle,
  FaRegFileAlt,
  FaListUl
} from "react-icons/fa";
import "../styles/ManageTimings.css";

const API = API_ENDPOINTS.TIMINGS;

const ManageTimings = () => {
  /* ================= STATE ================= */
  const [schedule, setSchedule] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [flow, setFlow] = useState([]);
  const [notes, setNotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [pageContent, setPageContent] = useState({
    title: "",
    subtitle: "",
  });

  // Tab State
  const [activeTab, setActiveTab] = useState("schedule"); // "details" | "schedule" | "highlights" | "flow" | "notes"

  /* ================= FETCH FROM BACKEND ================= */
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(API);
      const data = await res.json();

      if (data) {
        setPageContent(
          data.pageContent || {
            title: "",
            subtitle: "",
          }
        );
        setSchedule(data.schedule || []);
        setHighlights(data.highlights || []);
        setFlow(data.flow || []);
        setNotes(data.notes || []);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= UPDATE HELPERS ================= */
  const updateSchedule = (i, field, value) => {
    const updated = [...schedule];
    updated[i][field] = value;
    setSchedule(updated);
  };

  const updateHighlight = (i, field, value) => {
    const updated = [...highlights];
    updated[i][field] = value;
    setHighlights(updated);
  };

  const updateFlow = (i, field, value) => {
    const updated = [...flow];
    updated[i][field] = value;
    setFlow(updated);
  };

  const updateNote = (i, value) => {
    const updated = [...notes];
    updated[i] = value;
    setNotes(updated);
  };

  /* ================= ADD FUNCTIONS ================= */
  const addSchedule = () => {
    setSchedule([
      ...schedule,
      {
        time: "",
        activity: "",
        description: "",
      },
    ]);
  };

  const addHighlight = () => {
    setHighlights([
      ...highlights,
      {
        title: "",
        desc: "",
      },
    ]);
  };

  const addFlow = () => {
    setFlow([
      ...flow,
      {
        title: "",
        desc: "",
      },
    ]);
  };

  const addNote = () => {
    setNotes([...notes, ""]);
  };

  /* ================= DELETE FUNCTIONS ================= */
  const deleteSchedule = (i) => {
    setSchedule(schedule.filter((_, index) => index !== i));
  };

  const deleteHighlight = (i) => {
    setHighlights(highlights.filter((_, index) => index !== i));
  };

  const deleteFlow = (i) => {
    setFlow(flow.filter((_, index) => index !== i));
  };

  const deleteNote = (i) => {
    setNotes(notes.filter((_, index) => index !== i));
  };

  /* ================= SAVE TO BACKEND ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      await fetch(API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pageContent,
          schedule,
          highlights,
          flow,
          notes,
        }),
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Temple Timings Saved Successfully!");
      fetchData();
    } catch (err) {
      console.error("Save error:", err);
      alert("❌ Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="timings-loading">
        <div className="spinner"></div>
        <h2>Loading Temple Timings...</h2>
        <p>Syncing schedule records</p>
      </div>
    );
  }

  return (
    <div className="manage-timings-container">
      {/* BANNER SECTION */}
      <div className="timings-banner">
        <div className="banner-content">
          <h1>🕰️ Manage Temple Timings</h1>
          <p>Modify daily schedule, temple opening/closing times, spiritual rituals, special events, and descriptive notices.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaClock className="icon" />
            <span><strong>{schedule.length}</strong> Schedules</span>
          </div>
          <div className="stat-pill">
            <FaStar className="icon" />
            <span><strong>{highlights.length}</strong> Highlights</span>
          </div>
          <div className="stat-pill">
            <FaListUl className="icon" />
            <span><strong>{flow.length}</strong> Flow Stages</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & TAB WRAPPER */}
      <div className="dashboard-controls">
        <div className="tabs-wrapper">
          <button 
            className={`tab-btn ${activeTab === "schedule" ? "active" : ""}`}
            onClick={() => setActiveTab("schedule")}
          >
            <FaClock /> Daily Schedule
          </button>
          <button 
            className={`tab-btn ${activeTab === "highlights" ? "active" : ""}`}
            onClick={() => setActiveTab("highlights")}
          >
            <FaStar /> Highlights
          </button>
          <button 
            className={`tab-btn ${activeTab === "flow" ? "active" : ""}`}
            onClick={() => setActiveTab("flow")}
          >
            <FaListUl /> Daily Flow
          </button>
          <button 
            className={`tab-btn ${activeTab === "notes" ? "active" : ""}`}
            onClick={() => setActiveTab("notes")}
          >
            <FaRegFileAlt /> Spiritual Notes
          </button>
          <button 
            className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            <FaInfoCircle /> Page Details
          </button>
        </div>

        <div className="quick-action-buttons">
          {activeTab === "schedule" && (
            <button className="action-btn add-btn-primary" onClick={addSchedule}>
              <FaPlus /> Add Schedule
            </button>
          )}
          {activeTab === "highlights" && (
            <button className="action-btn add-btn-primary" onClick={addHighlight}>
              <FaPlus /> Add Highlight
            </button>
          )}
          {activeTab === "flow" && (
            <button className="action-btn add-btn-primary" onClick={addFlow}>
              <FaPlus /> Add Flow
            </button>
          )}
          {activeTab === "notes" && (
            <button className="action-btn add-btn-primary" onClick={addNote}>
              <FaPlus /> Add Note
            </button>
          )}
          <button 
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL CONTAINER */}
      <div className="panel-container">
        
        {/* PANEL: PAGE DETAILS */}
        {activeTab === "details" && (
          <div className="panel-card fade-in">
            <div className="panel-card-header">
              <h3><FaInfoCircle className="title-icon" /> Page Title & Settings</h3>
              <p>Configure the header texts that appear at the top of the timings web page.</p>
            </div>
            
            <div className="panel-card-body">
              <div className="form-group">
                <label>Page Title</label>
                <input
                  type="text"
                  value={pageContent.title}
                  onChange={(e) => setPageContent({ ...pageContent, title: e.target.value })}
                  placeholder="e.g. Mandir Timings"
                />
              </div>

              <div className="form-group">
                <label>Subtitle / Timings Message</label>
                <textarea
                  value={pageContent.subtitle}
                  onChange={(e) => setPageContent({ ...pageContent, subtitle: e.target.value })}
                  placeholder="e.g. Daily timings and details of Sai Baba Aarti and Abhishekams..."
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {/* PANEL: DAILY SCHEDULE */}
        {activeTab === "schedule" && (
          <div className="directory-section fade-in">
            {schedule.length === 0 ? (
              <div className="empty-state">
                <FaClock className="empty-icon" />
                <h3>No Schedules Configured</h3>
                <p>Create lists of daily timings (e.g., Morning Aarti, Noon Pujas) to display.</p>
                <button className="action-btn add-btn-primary" onClick={addSchedule}>
                  <FaPlus /> Add First Schedule
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {schedule.map((item, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">Schedule Entry #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteSchedule(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-row">
                        <div className="form-group-compact flex-medium">
                          <label>Time Slots</label>
                          <input
                            type="text"
                            value={item.time}
                            onChange={(e) => updateSchedule(i, "time", e.target.value)}
                            placeholder="e.g. 12:00 PM"
                          />
                        </div>

                        <div className="form-group-compact flex-grow">
                          <label>Activity / Ritual Name</label>
                          <input
                            type="text"
                            value={item.activity}
                            onChange={(e) => updateSchedule(i, "activity", e.target.value)}
                            placeholder="e.g. Madhyan Aarti"
                          />
                        </div>
                      </div>

                      <div className="form-group-compact">
                        <label>Description / Additional Info</label>
                        <textarea
                          value={item.description}
                          onChange={(e) => updateSchedule(i, "description", e.target.value)}
                          placeholder="e.g. Prasadam distributed immediately after worship..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PANEL: HIGHLIGHTS */}
        {activeTab === "highlights" && (
          <div className="directory-section fade-in">
            {highlights.length === 0 ? (
              <div className="empty-state">
                <FaStar className="empty-icon" />
                <h3>No Highlights Configured</h3>
                <p>Configure important items like opening hours, dress codes, or entry regulations.</p>
                <button className="action-btn add-btn-primary" onClick={addHighlight}>
                  <FaPlus /> Add First Highlight
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {highlights.map((item, i) => (
                  <div className="content-card highlight-glow" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number highlighted">✨ Highlight #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteHighlight(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Highlight Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateHighlight(i, "title", e.target.value)}
                          placeholder="e.g. Temple Opening Hours"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Description / Rules</label>
                        <textarea
                          value={item.desc}
                          onChange={(e) => updateHighlight(i, "desc", e.target.value)}
                          placeholder="e.g. 6:00 AM to 9:00 PM daily..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PANEL: DAILY FLOW */}
        {activeTab === "flow" && (
          <div className="directory-section fade-in">
            {flow.length === 0 ? (
              <div className="empty-state">
                <FaListUl className="empty-icon" />
                <h3>No Daily Flow Items</h3>
                <p>Create visual flow cards detailing typical pilgrimage routines.</p>
                <button className="action-btn add-btn-primary" onClick={addFlow}>
                  <FaPlus /> Add First Flow
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {flow.map((item, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">Daily Flow Step #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteFlow(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Flow Step Title</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateFlow(i, "title", e.target.value)}
                          placeholder="e.g. Step 1: Foot Wash"
                        />
                      </div>

                      <div className="form-group-compact">
                        <label>Step Instructions</label>
                        <textarea
                          value={item.desc}
                          onChange={(e) => updateFlow(i, "desc", e.target.value)}
                          placeholder="e.g. Wash your feet at the entrance taps before entry..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PANEL: NOTES */}
        {activeTab === "notes" && (
          <div className="directory-section fade-in">
            {notes.length === 0 ? (
              <div className="empty-state">
                <FaRegFileAlt className="empty-icon" />
                <h3>No Spiritual Notes</h3>
                <p>Add guidelines, notices, or spiritual mantras to display at the bottom of the page.</p>
                <button className="action-btn add-btn-primary" onClick={addNote}>
                  <FaPlus /> Add First Note
                </button>
              </div>
            ) : (
              <div className="cards-grid">
                {notes.map((note, i) => (
                  <div className="content-card" key={i}>
                    <div className="card-header-actions">
                      <span className="card-number">Spiritual Note #{i + 1}</span>
                      <button className="card-delete-btn" onClick={() => deleteNote(i)}>
                        <FaTrash /> Delete
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="form-group-compact">
                        <label>Note Content</label>
                        <textarea
                          value={note}
                          onChange={(e) => updateNote(i, e.target.value)}
                          placeholder="e.g. Cameras and mobile phones are strictly prohibited inside the main sanctum..."
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to temple timings will be discarded.</span>
        </div>
        <button 
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManageTimings;
